// @ts-strict-ignore
/** @jsx jsx */

import { jsx } from 'theme-ui'
import React, { useState, useEffect, useMemo } from 'react'

import { getField } from './utils'
import { executeScroll } from '../../utils/scroll'

import { Button } from '@uswitch/ev-components/lib/components/elements/trustyle-button'
import { Button as SparkButton } from '@uswitch/spark'

import SlideReveal from '@uswitch/ev-components/lib/components/elements/slide-reveal'
import DropDown from '@uswitch/ev-components/lib/components/elements/drop-down'
import { TruSort, TruMoneyFilter, TruCaret } from '@uswitch/spark-icons/tru'
import ConnectedRichText from '../rich-text-block'
import { brand } from '../../redux/app-selectors'

import {
  setQueryLoading,
  getData,
  setQueryErrored,
  GetDataType,
} from '../../redux/elasticsearch/elasticsearch-actions'
import { getModuleFromIncludes, includes, productTable } from '../../redux/contentful-selectors'
import { contentId, getProductTableWithRelations } from '../../utils/contentful'
import cleansedUserInputs from '../../utils/parse-calculator-inputs'
import { trackEvent, trackElementClicked, gtmTrack } from '../../utils/tracking'

import loadable from '@loadable/component'
import { connect } from 'react-redux'
import { FsCore } from '../../types/fs-core'
import { Contentful } from '../../types/contentful'
import { setPageOffset } from '../../redux/product-tables/product-table-actions'
import { getCalculator, getCalculatorInputs } from '../../redux/calculators/calculators-selectors'
import { getESData, visualFilterQueryId } from '../../redux/elasticsearch/elasticsearch-selectors'
import { findCalculatorSchema } from '../../utils/calculator-helpers'
import { findFiltersSchema } from '../../utils/filters-helpers'
import { getFilterGroup } from '../../redux/filters/filters-selectors'

import { setSort } from '../../redux/sorts/sort-actions'
import { useComponentTracking } from '../../utils/component-events'
import { getNoEligibilityResults } from '../../redux/product-tables/product-table-selectors'
import yieldToMain from '../../utils/yield-to-main'

const Filter = loadable(() => import('./filter-calculator'))
const Calculator = loadable(() => import('./calculator'))

export interface FilterBlockOptions {
  sorts?: Contentful.SortsSchema
  calculatorSchema?: Contentful.Calculator
  filtersSchema?: Contentful.FilterSchema[]
  calculatorId?: string
  filtersId?: string
  calculatorState: FsCore.CalculatorState
  filterGroupState: FsCore.FilterGroupState
  cleansedInputs: any
  extras?: { [key: string]: any }
  totalHits: number
  variant: string
  calcModalContent: any
  datasetId: string
  getData: GetDataType
  tracking: any
  productTableId: string
  visualFilterInState?: string
  dispatchSort: any
  selectedSort: any
  noEligibilityResults: boolean
  settings: any
}
const FilterBlock = ({
  sorts,
  calculatorSchema,
  filtersSchema,
  calculatorState,
  filterGroupState,
  cleansedInputs,
  extras,
  totalHits,
  variant,
  calcModalContent,
  datasetId,
  getData,
  tracking,
  productTableId,
  visualFilterInState,
  dispatchSort,
  selectedSort,
  noEligibilityResults,
  brand,
}: FilterBlockOptions) => {
  const scrollRef = React.createRef<HTMLDivElement>()

  const trackComponent = useComponentTracking()

  const overrideQuery = useMemo(
    () => (visualFilterInState ? { overrideQueryId: visualFilterInState } : {}),
    [visualFilterInState],
  )
  const onSort = (newSort: FsCore.SortState) => {
    dispatchSort(newSort)
    return getData({ newSort, productTableId, pageOffset: 0, ...overrideQuery })
  }

  const onFilter = (newFiltersArray: FsCore.FilterState[]) => {
    const newFilters: FsCore.FilterGroupState = {
      ...filterGroupState,
      filters: newFiltersArray,
    }
    for (const user of newFilters.usedBy) {
      getData({ newFilters, productTableId: user, pageOffset: 0, ...overrideQuery })
    }
  }

  const onCalculate = (newCalculator: FsCore.CalculatorState) => {
    const promises = []
    // We get data for each table using this calculator
    for (const user of newCalculator.usedBy) {
      promises.push(
        Promise.resolve(getData({ newCalculator, productTableId: user, pageOffset: 0 })),
      )
    }
    if (calculatorSchema?.usedBy?.length) {
      for (const tableId of calculatorSchema.usedBy) {
        promises.push(
          Promise.resolve(getData({ newCalculator, productTableId: tableId, pageOffset: 0 })),
        )
      }
    }
    return Promise.all(promises)
  }

  const shouldHideFilters = totalHits > 0 || noEligibilityResults

  const { isAlwaysVisible: isCalculatorAlwaysOpen } = calculatorSchema || {}

  const sortOptions =
    sorts && sorts.selectable && sorts.selectable.length
      ? [
          { value: '', text: 'Sort by' },
          ...sorts.selectable.map((sort) => {
            return { value: getField(sort.value)!, text: sort.label }
          }),
        ]
      : []

  const setUpFilter = (filters: Contentful.FilterSchema[] = []) => {
    return filters.map((filter) => ({
      label: filter.label,
      type: filter.type,
      name: filter.label.replace(/\s+/g, ''),
      options: filter.options.map((option) => ({
        label: option.label,
        value: option.label,
        name: option.label.replace(/\s+/g, ''),
      })),
    })) as FsCore.FilterState[]
  }

  const [filterOpen, setFilterOpen] = useState(false)
  const [calculatorOpen, setCalculatorOpen] = useState(
    !!isCalculatorAlwaysOpen || !shouldHideFilters,
  )

  const filterBy = !filtersSchema ? [] : setUpFilter(filtersSchema)

  useEffect(() => {
    setCalculatorOpen(isCalculatorAlwaysOpen || !shouldHideFilters)
  }, [shouldHideFilters, isCalculatorAlwaysOpen])

  const defaultSort: Contentful.SelectableSortSchema =
    sorts?.selectable?.find((sort) => sort.default) || ({} as Contentful.SelectableSortSchema)

  const [sortBy, setSortByState] = useState(getField(defaultSort.value) || '')

  useEffect(() => {
    if (sorts?.usedBy?.length && selectedSort?.value) {
      setSortByState(getField(selectedSort?.value)!)
      for (const tableId of sorts.usedBy) {
        getData({ newSort: selectedSort, productTableId: tableId, pageOffset: 0, ...overrideQuery })
      }
    }
  }, [selectedSort, getData, productTableId, overrideQuery, sorts?.usedBy])

  const getSortByObj = (sortBy: string) => {
    return sorts?.selectable?.find(({ value }) => getField(value) === sortBy)
  }

  const setSortBy = async (sortBy?: string) => {
    setQueryLoading({ isLoading: true, queryId: datasetId })
    executeScroll(scrollRef)
    await yieldToMain()
    if (!sortBy) return
    setSortByState(sortBy)

    if (typeof onSort === 'function' && sortBy) {
      const sortByObj = getSortByObj(sortBy)
      if (sortByObj) {
        const maybePromise = onSort(sortByObj)
        Promise.resolve(maybePromise).then(() => {
          setPageOffset({ id: productTableId, pageOffset: 0 })
          setQueryLoading({ isLoading: false, queryId: datasetId })
        })
      }
    }
  }

  const handleFilterClick = (data: FsCore.FilterState[]) => {
    executeScroll(scrollRef)
    const maybePromise = onFilter(data)
    Promise.resolve(maybePromise).then(() => {
      setFilterOpen(false)
      setPageOffset({ id: productTableId, pageOffset: 0 })
      setQueryLoading({ isLoading: false, queryId: datasetId })
    })
  }

  const updateFilterOptions = async (data: any, filterBy: FsCore.FilterState[]) => {
    setQueryLoading({ isLoading: true, queryId: datasetId })
    await yieldToMain()
    let finalFilterBy: FsCore.FilterState[] = filterBy
    for (const [key, value] of Object.entries(data)) {
      const changedFilter = finalFilterBy.find((filter) => filter.name === key)!

      let newFilter = changedFilter
      if (!Array.isArray(value)) {
        const changedOption = changedFilter?.options.find((option) => option.value === value)!

        const optionIndex = changedFilter.options.indexOf(changedOption)

        newFilter = {
          ...changedFilter,
          options: [
            ...changedFilter.options.map((o, i) =>
              i === optionIndex
                ? {
                    label: changedOption.label,
                    value: true,
                    name: changedOption.name,
                  }
                : o,
            ),
          ],
        }
      } else if (Array.isArray(value) && value.length > 0) {
        const changedOptions = value.map((val) => {
          const changedOption = changedFilter.options.find((option) => option.value === val)!

          return {
            ...changedOption,
            value: true,
          }
        })

        const newOptions = changedFilter.options.map(
          (obj) => changedOptions.find((o) => o?.label === obj.label) || obj,
        )
        newFilter = {
          ...changedFilter,
          options: newOptions,
        }
      }
      const filterIndex = finalFilterBy.indexOf(changedFilter)
      finalFilterBy = finalFilterBy.map((f, i) => (i === filterIndex ? newFilter : f))
    }
    handleFilterClick(finalFilterBy)
  }

  const handleCalculatorClick = (data: FsCore.CalculatorState) => {
    executeScroll(scrollRef)
    !isCalculatorAlwaysOpen && setCalculatorOpen(!calculatorOpen)
    setQueryLoading({ isLoading: true, queryId: datasetId })
    const promise = onCalculate(data)
    return promise
      .then(() => {
        setPageOffset({ id: productTableId, pageOffset: 0 })
        setQueryLoading({ isLoading: false, queryId: datasetId })
      })
      .catch((err) => {
        // eslint-disable-next-line
        console.error(`MENG736 - calculator-error "${err.message}"`)
        //        console.error(
        //          JSON.stringify({
        //            MENG736: {
        //              message: err.message,
        //              stackTrace: err.stack.split('\n'),
        //            },
        //          }),
        //        )
        setQueryLoading({ isLoading: false, queryId: datasetId })
        setQueryErrored({ isErrored: true })
        return Promise.reject(err)
      })
  }

  const filterText = 'Refine results'
  const sortText = 'Sort'

  const handleFilterTracking = () => {
    trackEvent(tracking?.filters)
    trackComponent('Product Table Event', 'filterClicked', { event: filterText })
    trackElementClicked('filters', filterText, 'button')
  }

  const handleSortTracking = () => {
    trackEvent(tracking?.sorts)
    trackComponent('Product Table Event', 'sortClicked', { event: sortText })
    trackElementClicked('sorts', sortText, 'button')
  }

  const handleFilterButton = () => {
    handleFilterTracking()
    setCalculatorOpen(false)
    setFilterOpen(!filterOpen)
  }

  const handleCalculatorButton = () => {
    setFilterOpen(false)
    trackComponent('Product Table Event', 'filterClicked', { event: filterText })
    setCalculatorOpen(!calculatorOpen)
  }

  const replaceObject = {
    resultCount: totalHits,
    resultOrResults: totalHits === 1 ? 'result' : 'results',
    sortBy: getSortByObj(sortBy)?.label.toLowerCase() || sortBy,
    complianceLink: extras?.complianceLink,
    additionalComplianceLink: extras?.additionalComplianceLink,
    ...cleansedInputs,
  }

  const complianceText =
    extras &&
    extras.complianceText &&
    extras.complianceText.replace(/\$\{([a-z]+)\}/gi, (original: string, match: string) => {
      return match in replaceObject ? replaceObject[match] : original
    })

  const calculatorButtonColor = calculatorOpen ? 'grey-05' : 'none'

  const legacyVariant =
    variant === 'vanilla' ||
    variant === 'Legacy' ||
    variant === 'eligibilityResultsLegacy' ||
    variant === 'reducedSpacingOne' ||
    variant === 'reducedSpacingTwo'

  const variantToTheme = {
    rich: 'default',
    Card: 'panel',
    Legacy: 'panel',
    vanilla: 'panel',
    eligibilityResultsLegacy: 'panel',
    Rich: 'slide',
  }

  const SortPills = () => {
    const pillSortOptions = sorts?.selectable?.map((sort, index) => {
      const isSortSelected = sortBy === getField(sort.value)!
      return (
        <SparkButton
          sx={{
            paddingY: 'xxxs',
            paddingX: 'sm',
            border: !isSortSelected ? 'none' : 'solid 1px',
            borderColor: !isSortSelected ? 'grey-40' : 'button-secondary',
            fontSize: 'xs',
            backgroundColor: !isSortSelected ? 'grey-10' : 'button-secondary',
            color: !isSortSelected ? 'button-secondary' : 'grey-0',
            ':hover': {
              backgroundColor: 'button-secondary',
              color: 'grey-0',
            },
          }}
          key={index}
          onClick={() => {
            setSortBy(getField(sort.value)!)
            gtmTrack('Slider - Filter Applied', 'Functional Interaction', sort.label)
          }}
        >
          {sort.label}
        </SparkButton>
      )
    })
    return (
      <div
        sx={{
          display: 'flex',
          overflowX: 'auto',
          pb: 'xxs',
          button: { marginRight: 'xxs', ':last-of-type': { marginRight: '0' } },
        }}
        ref={scrollRef}
      >
        {pillSortOptions}
      </div>
    )
  }

  return (
    <React.Fragment>
      <If
        condition={
          variant === 'eligibilitySlider' &&
          totalHits > 0 &&
          sorts?.selectable?.length &&
          (complianceText ||
            (filterBy.length && onFilter) ||
            (sortOptions.length && onSort && !sorts?.disableSortUi))
        }
      >
        <div
          sx={{
            display: 'flex',
            flexDirection: 'column',
            fontSize: 'sm',
            fontWeight: 'bolder',
            span: { marginBottom: 'sm', color: 'button-secondary', fontSize: ['xxs', 'sm'] },
            paddingLeft: ['xxs', '0'],
          }}
        >
          <span>Change your ranking here:</span>
          <SortPills />
        </div>
      </If>
      <If
        condition={
          variant !== 'eligibilitySlider' &&
          (complianceText ||
            (filterBy.length && onFilter) ||
            (sortOptions.length && onSort && !sorts?.disableSortUi))
        }
      >
        <div
          sx={{
            display: [null, 'flex'],
            alignItems: 'center',
            justifyContent: !complianceText ? 'flex-end' : 'space-between',
            paddingBottom: legacyVariant ? 5 : 9,
            variant:
              variant === 'Rich'
                ? 'compounds.product-table.variants.ordering.first'
                : 'compounds.product-table.variants.ordering.second',
          }}
          ref={scrollRef}
        >
          <If condition={complianceText}>
            <div
              id='product-table-compliance-text'
              sx={{
                marginRight: 'auto',
                fontSize: 'xs',
                color: legacyVariant && '#5d6a72',
                a: {
                  color: legacyVariant && 'text',
                },
                textAlign: legacyVariant && ['center', 'left'],
              }}
              dangerouslySetInnerHTML={{ __html: complianceText }}
            />
          </If>
          <If
            condition={
              (filterBy.length && onFilter) ||
              (sortOptions.length && onSort) ||
              (calculatorSchema && onCalculate && calculatorSchema.steps)
            }
          >
            <div
              sx={{
                display: calculatorSchema?.collapsibleFields ? 'none' : 'flex',
                flexShrink: 0,
                marginLeft: [null, 'md'],
                marginTop: ['sm', 0],
                justifyContent: legacyVariant && 'center',
              }}
            >
              <If condition={filterBy.length && onFilter && !extras?.disableFilterUI}>
                <div
                  sx={{
                    display: 'inline-block',
                    marginRight: sortOptions.length && onSort ? 'sm' : null,
                  }}
                >
                  <Button
                    variant='control'
                    data-product-table-action='toggle-filter-block'
                    sx={{
                      fontSize: 'xs',
                      whiteSpace: 'nowrap',
                      color: legacyVariant && '#34454e',
                      maxWidth: legacyVariant && '145px',
                      justifyContent: legacyVariant && 'center',
                      padding: legacyVariant && '10px 15px',
                    }}
                    onClick={() => handleFilterButton()}
                  >
                    <div sx={{ display: 'inline-block', width: '1em', marginRight: 'xs' }}>
                      <TruMoneyFilter color={brand === 'money' ? '#DB4D75' : '#141414'} />
                    </div>
                    {filterText}
                  </Button>
                </div>
              </If>
              <If
                condition={
                  !isCalculatorAlwaysOpen &&
                  calculatorSchema &&
                  onCalculate &&
                  calculatorSchema.steps
                }
              >
                <div
                  sx={{
                    display: 'inline-block',
                    marginRight: sortOptions.length && onSort ? 'sm' : null,
                  }}
                >
                  <Button
                    variant='control'
                    data-product-table-action='toggle-calculator-block'
                    sx={{
                      fontSize: 'xs',
                      whiteSpace: 'nowrap',
                      backgroundColor: calculatorButtonColor,
                    }}
                    onClick={() => handleCalculatorButton()}
                  >
                    <div sx={{ display: 'inline-block', width: '1em', marginRight: 'xs' }}>
                      <TruMoneyFilter color={brand === 'money' ? '#DB4D75' : '#141414'} />
                    </div>
                    {filterText}
                  </Button>
                </div>
              </If>
              <If condition={sortOptions.length && onSort && !sorts?.disableSortUi}>
                <div
                  sx={{
                    display: 'inline-block',
                    background: 'white',
                  }}
                >
                  <DropDown
                    name='sorts'
                    onBlur={() => {}}
                    onChange={(val: any) => {
                      setSortBy(val)
                      trackComponent('Product Table Event', 'sortApplied', {
                        event: sortOptions.find((i) => i.value === val)?.text,
                      })
                    }}
                    options={sortOptions}
                    value={sortBy}
                    onClick={handleSortTracking}
                    overlay={
                      <span
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          width: '100%',
                          justifyContent: brand === 'money' ? 'center' : 'space-between',
                        }}
                      >
                        <div sx={{ display: 'flex', alignItems: 'center' }}>
                          <TruSort color={brand === 'money' ? '#DB4D75' : '#141414'} />
                          <span sx={{ marginLeft: 'xs' }}>{sortText}</span>
                        </div>

                        {(brand === 'uswitch' || brand === 'confused') && (
                          <div sx={{ display: 'flex', width: '1em', marginLeft: 'xs' }}>
                            <TruCaret
                              color={brand === 'confused' ? '#1f1f1f' : '#141414'}
                              sx={{ transform: 'rotate(0.5turn)' }}
                            />
                          </div>
                        )}
                      </span>
                    }
                    sx={{
                      borderColor: brand === 'confused' ? '#EA9665' : 'grey-20',
                      fontSize: 'xs',
                      color: legacyVariant && '#34454e',
                      maxWidth: legacyVariant && '145px',
                      justifyContent: legacyVariant && 'center',
                      padding: legacyVariant && '10px 15px',
                    }}
                  />
                </div>
              </If>
            </div>
          </If>
        </div>
      </If>
      <If condition={filterBy.length && onFilter && !extras?.disableFilterUI}>
        <SlideReveal open={filterOpen} initialOpen={false} className='filter-block'>
          <Filter
            variant={variant}
            filterBy={filterBy}
            updateFilterOptions={updateFilterOptions}
            gtmTrack={gtmTrack}
          />
        </SlideReveal>
      </If>

      <If
        condition={
          calculatorSchema &&
          onCalculate &&
          calculatorSchema.steps &&
          !calculatorSchema.collapsibleFields &&
          !calculatorSchema.calculatorType
        }
      >
        <div
          sx={{
            variant: 'compounds.product-table.variants.ordering.first',
          }}
        >
          <SlideReveal open={calculatorOpen} initialOpen={false} className='calculator-block'>
            <Calculator
              calculatorState={calculatorState}
              calculatorSchema={calculatorSchema}
              submit={handleCalculatorClick}
              modalContent={calcModalContent}
              variant={variantToTheme[variant]}
              trackInteraction={gtmTrack}
              resultsAvailable={shouldHideFilters}
            />
          </SlideReveal>
        </div>
      </If>
    </React.Fragment>
  )
}

export interface ConnectFilterBlockParams {
  calculatorModalContent: any
  variant: string
  datasetId: string
  productTableSchemaId: string
  productTableId: string
  [key: string]: any
}

const ConnectFilterBlock = connect(
  (
    state: FsCore.State,
    {
      calculatorModalContent,
      productTableSchemaId,
      datasetId,
      productTableId,
      ...rest
    }: ConnectFilterBlockParams,
  ) => {
    const calcModalId = contentId(calculatorModalContent)
    const calcModalComp = getModuleFromIncludes(includes(state), calcModalId)
    const calcModalContent = calcModalId && (
      <ConnectedRichText content={calcModalComp.fields.content} />
    )
    const schemaComponent = productTable(state, productTableSchemaId)

    const schemaObject = schemaComponent.fields.schema
    const calculatorId = schemaObject.calculatorId
    const filtersId = schemaObject.filtersId
    const calculatorSchema = findCalculatorSchema(state, schemaComponent)
    const filtersSchema = findFiltersSchema(state, schemaComponent)
    const calculatorState = getCalculator(state, calculatorId!)!
    const filterGroupState = getFilterGroup(state, filtersId!)!
    const calculatorInputs = getCalculatorInputs(state, calculatorId!)
    const cleansedInputs = cleansedUserInputs(calculatorInputs)
    const totalHits = getESData(state, datasetId)?.hits?.total || 0
    const visualFilterInState = visualFilterQueryId(state)
    const { productDataset } = getProductTableWithRelations(state, productTableId)
    const noEligibilityResults = getNoEligibilityResults(state, productDataset!.sys.id)

    return {
      ...rest,
      ...schemaObject,
      calculatorSchema,
      filtersSchema,
      cleansedInputs,
      calculatorState,
      filterGroupState,
      totalHits,
      calcModalContent,
      datasetId,
      calculatorId,
      filtersId,
      visualFilterInState,
      selectedSort: state.sorts.selectedSort,
      noEligibilityResults,
      brand: brand(state),
    }
  },
  // No idea why getData isn't compatible with its own type...
  { getData: getData as any, dispatchSort: setSort as any },
)(FilterBlock)

export default (props: any) => <ConnectFilterBlock {...props} />
